/**
 * V106-1-8 / V106-5-6 — pure view models for the SOC 2 and ISO 42001 screens
 * (design pass `v106-program-screens`, round 3).
 *
 *   tree         readiness → groups (CC6, clause 6, A.6) → criteria → objectives,
 *                in document order, each objective classified with the SAME
 *                rule the server's next-gap ranker uses (core objectiveKind);
 *   anchors      `#pof-cc61-05` (SOC 2), `#obj-612-02` / `#obj-a624` (ISO);
 *   todayCard    the D8 card: one large number by stage, the override strip,
 *                "Then · …" under it, pace, meter, the "✕ N not met" chip.
 *
 * D8 build rule (ADR-0078 note): A Today status card shows ONE large number,
 * chosen by the program's stage. Prepare: readiness always (covered / total,
 * pace, 3-part meter, "✕ N not met"); a problem that outranks the next gap goes
 * in the next-step slot as a status-coloured strip with its own filled action
 * (2–4-word headline, dated line, consequence in the status colour) and the gap
 * stays under it as "Then · …". Ranking: Class A expired / opinion not
 * unmodified / report issued-not-attached (only when the workspace runs FedRAMP
 * Class A) > Not met > No evidence > To decide; "Expiring" is only an "Also:"
 * line. Operate: a dated problem takes the large number ("12 days late") with
 * the consequence under it; the action is the fix, the secondary the record
 * path. Decision / setup items take a short noun phrase, never a percentage.
 * Only the top-ranked card on Today fills its button.
 */
import {
  compareDocumentOrder, objectiveDisplayLabel, objectiveKind, requirementGroup,
  type MissingReason, type ObjectiveKind, type ObjectiveReadiness, type ObjectiveReadinessRow, type ProgramTodayCard,
} from '@aigovops/core';

// ---- names (short, generic; no AICPA / ISO text) -------------------------------------------

export const SOC2_SERIES: Readonly<Record<string, string>> = {
  CC1: 'Control environment', CC2: 'Communication and information', CC3: 'Risk assessment', CC4: 'Monitoring activities',
  CC5: 'Control activities', CC6: 'Logical and physical access', CC7: 'System operations', CC8: 'Change management', CC9: 'Risk mitigation',
  A1: 'Availability', C1: 'Confidentiality', PI1: 'Processing integrity',
  P1: 'Privacy notice', P2: 'Choice and consent', P3: 'Collection', P4: 'Use, retention and disposal', P5: 'Access', P6: 'Disclosure and notification',
  P7: 'Quality', P8: 'Monitoring and enforcement',
};
export const ISO_GROUPS: Readonly<Record<string, string>> = {
  4: 'Context of the organization', 5: 'Leadership', 6: 'Planning', 7: 'Support', 8: 'Operation', 9: 'Performance evaluation', 10: 'Improvement',
  'A.2': 'AI policy', 'A.3': 'Internal organization', 'A.4': 'Resources for AI systems', 'A.5': 'Impact assessment', 'A.6': 'AI system life cycle',
  'A.7': 'Data for AI systems', 'A.8': 'Information for interested parties', 'A.9': 'Use of AI systems', 'A.10': 'Third parties and customers',
};
export const SOC2_CATEGORIES = [
  { key: 'availability', label: 'Availability', series: ['A1'] },
  { key: 'confidentiality', label: 'Confidentiality', series: ['C1'] },
  { key: 'processing_integrity', label: 'Processing Integrity', series: ['PI1'] },
  { key: 'privacy', label: 'Privacy', series: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8'] },
] as const;

export const groupName = (program: string, group: string) => (program === 'soc2' ? SOC2_SERIES[group] : ISO_GROUPS[group]) ?? group;

/** Deep-link anchor of an objective row (`#pof-cc61-05`, `#obj-612-02`, `#obj-a624`). */
export function objectiveAnchor(program: string, label: string): string {
  const m = /^(.*?)-(POF|OBJ)-(\d+)$/.exec(label);
  if (m) return `${m[2] === 'POF' ? 'pof' : 'obj'}-${m[1]!.toLowerCase().replace(/[^a-z0-9]/g, '')}-${m[3]}`;
  return `${program === 'soc2' ? 'pof' : 'obj'}-${label.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
}

// ---- the tree ------------------------------------------------------------------------------

export type OpenKind = Exclude<ObjectiveKind, 'met' | 'na'>;
export interface TreeObjective { row: ObjectiveReadinessRow; kind: ObjectiveKind; label: string; anchor: string }
export interface TreeCriterion { requirement_id: string; ref: string; title: string; status: string; objectives: TreeObjective[]; covered: number; total: number; open: Record<OpenKind, number> }
export interface TreeGroup { group: string; name: string; criteria: TreeCriterion[]; open: Record<OpenKind, number>; openCriteria: number; total: number }
export interface Tree { groups: TreeGroup[]; counts: { met: number; na: number; not: number; noev: number; dec: number; total: number; covered: number }; criteria: { met: number; open: number; total: number } }

const zero = (): Record<OpenKind, number> => ({ not: 0, noev: 0, dec: 0 });

export function buildTree(r: Pick<ObjectiveReadiness, 'program' | 'requirements'>): Tree {
  const program = r.program;
  const counts = { met: 0, na: 0, not: 0, noev: 0, dec: 0, total: 0, covered: 0 };
  const groups = new Map<string, TreeGroup>();
  const reqs = [...r.requirements].sort((a, b) => compareDocumentOrder(program, a.ref, b.ref));
  let critMet = 0;
  for (const q of reqs) {
    const g = requirementGroup(program, q.ref);
    const objectives = [...q.objectives].sort((a, b) => compareDocumentOrder(program, a.ref, b.ref)).map((row) => {
      const kind = objectiveKind(row);
      const label = objectiveDisplayLabel(program, row.ref);
      return { row, kind, label, anchor: objectiveAnchor(program, label) };
    });
    const open = zero();
    let covered = 0;
    for (const o of objectives) {
      counts.total++;
      if (o.kind === 'met' || o.kind === 'na') { covered++; counts.covered++; counts[o.kind]++; } else { open[o.kind]++; counts[o.kind]++; }
    }
    const crit: TreeCriterion = { requirement_id: q.requirement_id, ref: q.ref, title: q.title, status: q.status, objectives, covered, total: objectives.length, open };
    if (covered === objectives.length && objectives.length > 0) critMet++;
    const tg = groups.get(g) ?? { group: g, name: groupName(program, g), criteria: [], open: zero(), openCriteria: 0, total: 0 };
    tg.criteria.push(crit);
    tg.total++;
    if (covered < objectives.length) tg.openCriteria++;
    for (const k of ['not', 'noev', 'dec'] as const) tg.open[k] += open[k];
    groups.set(g, tg);
  }
  return { groups: [...groups.values()], counts, criteria: { met: critMet, open: reqs.length - critMet, total: reqs.length } };
}

/** "1 not met · 2 no evidence" chips (text, never colour alone). */
export function chipList(open: Record<OpenKind, number>): Array<{ k: OpenKind; text: string }> {
  const out: Array<{ k: OpenKind; text: string }> = [];
  if (open.not) out.push({ k: 'not', text: `${open.not} not met` });
  if (open.noev) out.push({ k: 'noev', text: `${open.noev} no evidence` });
  if (open.dec) out.push({ k: 'dec', text: `${open.dec} to decide` });
  return out;
}

export const KIND_WORD: Record<ObjectiveKind, string> = { met: 'Met', na: 'Not applicable', not: 'Not met', noev: 'No evidence', dec: 'To decide' };
export const KIND_GAP_LABEL: Record<OpenKind, string> = { not: '✕ Not met', noev: 'No evidence', dec: 'To decide' };

// ---- dates ---------------------------------------------------------------------------------

const DAY = 86_400_000;
export const dayDiff = (from: string, to: string) => Math.round((Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / DAY);
/** "Dec 31" in the current year, else "Feb 26, 2027". */
export function fmtDate(d: string, today: string): string {
  const dt = new Date(`${d.slice(0, 10)}T00:00:00Z`);
  const sameYear = d.slice(0, 4) === today.slice(0, 4);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }), timeZone: 'UTC' });
}
export function daysWord(n: number): string { return `${Math.abs(n)} ${Math.abs(n) === 1 ? 'day' : 'days'}`; }
/** "79 days" / "12 days ago" / "today". */
export function relDays(to: string, today: string): string {
  const n = dayDiff(today, to);
  return n === 0 ? 'today' : n > 0 ? daysWord(n) : `${daysWord(n)} ago`;
}

// ---- Today card (D8) -----------------------------------------------------------------------

export interface TodayCardModel {
  program: string;
  title: string;
  status: 'on_track' | 'at_risk' | 'off_track' | 'needs_you' | 'needs_setup';
  statusDetail: string | null;
  eyebrow: string;
  metric: { big: string; small: string };
  pace: { kind: 'delta'; gained: number; needed: number; behind: boolean } | { kind: 'switch'; on: string; needed: number | null } | { kind: 'open'; gained: number | null; open: number } | null;
  meter: { met: number; notMet: number; open: number; /** This week's target (covered at the start of the week + needed per week), glance H. */ target: number | null } | null;
  notMet: number | null;
  inProgress: number;
  consequence: string | null;
  date: string;
  /** D8 override strip; its own filled action sits in the strip (glance H: the button next to the problem it fixes). */
  override: { headline: string; dated: string; consequence: string; tone: 'warn' | 'bad'; action: { label: string; href: string } | null } | null;
  next: { key: string; tone: 'bad' | null; text: string; ariaLabel: string; title: string; href: string } | null;
  also: { text: string; href: string } | null;
  /** D-106-18: proposed results this viewer may review ("Review 3 proposed results"). */
  review: { text: string; href: string } | null;
  /** Switch week: the rule set and how to revert (glance H). */
  revert: { text: string; href: string } | null;
  /** A line under the metric when there is no one to ask (owner decision 2026-10-14). */
  note: string | null;
  action: { label: string; href: string; kind: 'link' } | { label: string; kind: 'ask'; askUserId: string; about: string };
  secondary: { text: string; href: string } | null;
}

export interface TodayCardContext {
  prefix: string;
  today: string;
  title: string;
  /** The viewer's workspace role (cycle rules: admins approve, everyone else asks). */
  isAdmin: boolean;
  /** The user to ask (an admin) and their name. */
  askAdmin: { user_id: string; name: string } | null;
  names: ReadonlyMap<string, string>;
}

export function todayCard(c: ProgramTodayCard, ctx: TodayCardContext): TodayCardModel | null {
  if (c.program === 'soc2' && c.engine !== 'objective') return null;
  const base = `${ctx.prefix}/programs/${c.program}`;
  const t = ctx.today;
  const prog = c.program === 'soc2' ? 'SOC 2' : 'ISO 42001';
  const r = c.readiness;
  const stageWord = c.stage.charAt(0).toUpperCase() + c.stage.slice(1);
  const eyebrow = `${stageWord}${c.period ? ` · ${c.period.label}` : ''}`;
  const model: TodayCardModel = {
    program: c.program, title: ctx.title, status: c.status, statusDetail: c.reason, eyebrow,
    metric: { big: '', small: '' }, pace: null, meter: null, notMet: null, inProgress: 0, consequence: null, date: '', override: null, next: null, also: null, review: null, revert: null, note: null,
    action: { label: `Open ${prog}`, href: base, kind: 'link' }, secondary: null,
  };
  // Operate problem: the dated problem takes the large number (D8).
  if (c.variant === 'operate_problem' && c.late) {
    // Glance H: one date source — the days come from the same due date and today as the Operate tab.
    model.metric = { big: `${daysWord(Math.max(0, dayDiff(c.late.due_on, t)))} late`, small: c.late.label.toLowerCase() };
    model.consequence = 'The certificate can be suspended';
    model.date = `Was due ${fmtDate(c.late.due_on, t)}`;
    model.action = { label: 'Book with the certification body', href: `${base}/operate#book`, kind: 'link' };
    model.secondary = { text: 'It happened · record the date', href: `${base}/operate#ms-h` };
    return model;
  }
  if (c.variant === 'needs_you' && c.soa) {
    const ch = c.soa.changes[0];
    const more = c.soa.changes.length - 1;
    model.eyebrow = `Scope · SoA v${c.soa.version}`;
    model.metric = ch
      ? { big: `${ch.title || ch.ref} ${ch.to === 'applicable' ? 'back in scope' : 'excluded'}${more > 0 ? ` +${more}` : ''}`, small: `${ch.ref} · ${ch.to === 'applicable' ? '+1 objective' : '−1 objective'}` }
      : { big: `SoA v${c.soa.version}`, small: 'waiting for you' };
    model.date = `Proposed by ${ctx.names.get(c.soa.authored_by) ?? 'a teammate'} · ${relDays(c.soa.authored_at, t) === 'today' ? 'today' : fmtDate(c.soa.authored_at, t)}`;
    model.action = { label: `Review SoA v${c.soa.version}`, href: `${base}/scope#soa-v`, kind: 'link' };
    return model;
  }
  // Readiness (Prepare, or Operate once certified).
  if (r) {
    const notMet = r.counts.not_met;
    model.metric = { big: `${r.covered} / ${r.total}`, small: c.program === 'soc2' ? 'points covered' : 'objectives met' };
    const target = c.pace && c.pace.needed_per_week > 0 ? Math.min(r.total, r.covered - (c.delta?.gained ?? 0) + c.pace.needed_per_week) : null;
    model.meter = { met: r.covered, notMet, open: r.total - r.covered - notMet, target };
    model.notMet = notMet;
    model.inProgress = r.counts.in_progress;
    const open = r.total - r.covered;
    if (c.counting_changed_on) {
      model.pace = { kind: 'switch', on: fmtDate(c.counting_changed_on, t), needed: null };
      // Glance M (round 2): the status reason says why it is at risk (the pace), not the open count the big number shows.
      if (c.pace?.needed_per_week && c.hard_date && c.status !== 'on_track') model.statusDetail = `needs ~${c.pace.needed_per_week}/wk to ${fmtDate(c.hard_date.date, t)}`;
      if (c.program === 'soc2') model.revert = { text: 'Counting rule and how to revert', href: `${ctx.prefix}/queue?rule_set=soc2-readiness` };
    } else if (open === 0) model.pace = null;
    else if (c.delta && c.pace && c.pace.needed_per_week > 0) model.pace = { kind: 'delta', gained: c.delta.gained, needed: c.pace.needed_per_week, behind: !!c.pace.behind };
    else model.pace = { kind: 'open', gained: c.delta?.gained ?? null, open };
  } else {
    model.metric = { big: 'Not counted yet', small: c.period ? 'readiness is loading' : 'set the audit window' };
  }
  model.date = c.hard_date ? `${c.hard_date.label} ${fmtDate(c.hard_date.date, t)} · ${relDays(c.hard_date.date, t)}` : c.period ? `${c.period.label}` : 'No window yet';
  if (c.next) {
    const anchor = objectiveAnchor(c.program, c.next.label);
    const key = c.next.concern ? 'Stage 1 concern' : c.next.quick_win && c.next.kind === 'noev' && !c.override ? 'Quick win' : KIND_GAP_LABEL[c.next.kind].replace('✕ ', '');
    const tone = c.next.kind === 'not' ? 'bad' as const : null;
    const keyWord = `${key}`;
    const href = `${base}/prepare${c.next.quick_win && key === 'Quick win' ? '?panel=connect' : ''}#${anchor}`;
    model.next = { key: c.override ? `Then · ${keyWord}` : keyWord, tone: c.override ? null : tone, text: c.next.statement, ariaLabel: `${c.override ? 'Then, ' : ''}${keyWord.toLowerCase()}: ${c.next.statement}, ${c.next.label}`, title: `${c.next.statement} · ${c.next.label}`, href };
    if (c.next.concern && c.concerns) model.action = { label: `Close concern ${c.concerns.total - c.concerns.open + 1} of ${c.concerns.total}`, href, kind: 'link' };
    else if (key === 'Quick win' && c.next.quick_win && ctx.isAdmin) model.action = { label: `Collect from ${connectorWord(c.next.quick_win.kind)}`, href, kind: 'link' };
    else if (key === 'Quick win') model.action = { label: 'Add evidence for the next gap', href: href.replace('?panel=connect', ''), kind: 'link' };
    else model.action = { label: c.next.kind === 'not' ? 'Fix the next gap' : c.next.kind === 'noev' ? 'Add evidence for the next gap' : 'Decide the next gap', href, kind: 'link' };
  } else if (r) {
    model.action = { label: c.stage === 'operate' ? `Open ${prog}` : 'Review readiness', href: `${base}/${c.stage === 'operate' ? 'operate' : 'prepare'}`, kind: 'link' };
  }
  // D8 override strip (SOC 2, only with FedRAMP Class A).
  if (c.override) {
    if (c.override.kind === 'report_not_on_file') {
      model.override = { headline: 'Report not on file', dated: `${c.override.period_label ?? ''}${c.override.date ? ` · issued ${fmtDate(c.override.date, t)} · ${relDays(c.override.date, t)}` : ''}`.replace(/^ · /, ''), consequence: 'Blocks FedRAMP Class A', tone: 'warn',
        action: { label: 'Attach the report PDF', href: `${base}/audit?attach=1#attach` } };
    } else if (c.override.kind === 'class_a_expired') {
      model.override = {
        headline: 'SOC 2 expired for Class A', dated: c.override.date ? `Expired ${fmtDate(c.override.date, t)} · ${relDays(c.override.date, t)}` : 'Expired',
        consequence: c.override.until ? `No Class A until ${fmtDate(c.override.until, t)} · ${relDays(c.override.until, t)}` : 'No Class A until a new report is attached', tone: 'bad',
        action: { label: 'Plan the next report', href: `${base}/operate#class-a` },
      };
    } else {
      model.override = { headline: 'Opinion not unmodified', dated: 'The SOC 2 report has a qualified opinion', consequence: "Doesn't count for FedRAMP Class A", tone: 'bad',
        action: { label: 'Plan the fixes', href: `${ctx.prefix}/findings?program=soc2` } };
    }
  }
  // Without a gap under it, the strip's action is the card's action (no second button).
  if (model.override && !model.next && model.override.action) { model.action = { ...model.override.action, kind: 'link' }; model.override.action = null; }
  // Flow H (round 2) / D8: unapproved ISO cycle rules are a Prepare-stage override; readiness stays the big number.
  // Owner decision 2026-10-14: admins approve in the queue, others ask an admin, no admin → an owner approves (Team).
  if (c.variant === 'needs_setup') {
    model.override = {
      headline: 'Cycle rules need approval', dated: 'Stage 2 can’t be booked yet',
      consequence: 'Stage 2 and the certification decision can’t be dated', tone: 'warn',
      action: ctx.isAdmin ? { label: 'Approve the cycle rules', href: `${ctx.prefix}/queue?rule_set=iso-certification&return=${encodeURIComponent(`${ctx.prefix}/today`)}` } : null,
    };
    model.statusDetail = null; // the strip names it; no repeat beside the status
    if (c.next) model.next = { ...model.next!, key: `Then · ${model.next!.key}`, tone: null, ariaLabel: `Then, ${model.next!.ariaLabel}` };
    if (!ctx.isAdmin && ctx.askAdmin) model.action = { label: `Ask ${ctx.askAdmin.name.split(/\s+/)[0]} to approve`, kind: 'ask', askUserId: ctx.askAdmin.user_id, about: 'Approve the ISO 42001 certification-cycle rules' };
    else if (!ctx.isAdmin) { model.note = 'An owner approves the cycle rules'; model.action = { label: 'Open Settings · Team', href: `${ctx.prefix}/settings/team`, kind: 'link' }; }
  }
  if (c.also?.kind === 'class_a_expiring' && c.also.date) model.also = { text: `Class A expires ${fmtDate(c.also.date, t)} · ${relDays(c.also.date, t)}`, href: `${base}/operate#class-a` };
  else if (c.also?.kind === 'class_a_schedule') model.also = { text: 'Class A · upload the CPA schedule', href: `${base}/operate#class-a` };
  // D-106-18: results waiting on this viewer's review (never their own proposals; the server counts them).
  if (c.review && c.review.count > 0) {
    const n = c.review.count;
    model.review = { text: `${n} proposed ${n === 1 ? 'result' : 'results'} to approve`, href: `${base}/prepare?panel=review` };
  }
  // A decision waiting for this viewer outranks the Class A schedule reminder.
  if (c.risk_to_decide && (!model.also || c.also?.kind === 'class_a_schedule')) {
    const n = c.risk_to_decide.count;
    model.also = { text: `${n} risk ${n === 1 ? 'acceptance' : 'acceptances'} to decide`, href: `${base}/prepare#${objectiveAnchor(c.program, c.risk_to_decide.label)}` };
  }
  return model;
}

const CONNECTOR_WORD: Record<string, string> = { aws: 'AWS', aws_oidc: 'AWS', aws_role: 'AWS', gcp: 'Google Cloud', okta_posture: 'Okta', entra_posture: 'Entra ID', tenable: 'Tenable', datadog: 'Datadog' };
export const connectorWord = (kind: string | null | undefined) => (kind ? CONNECTOR_WORD[kind] ?? kind : 'connector');

/** Today order among these cards: off track > at risk / needs setup > needs you > on track; then the nearest hard date. */
export function rankTodayCards(cards: readonly ProgramTodayCard[]): ProgramTodayCard[] {
  const w: Record<ProgramTodayCard['status'], number> = { off_track: 0, at_risk: 1, needs_setup: 1, needs_you: 2, on_track: 3 };
  return [...cards].sort((a, b) => w[a.status] - w[b.status] || (a.hard_date?.date ?? '9999') .localeCompare(b.hard_date?.date ?? '9999'));
}

// ---- D-106-19: what a point is missing, in plain words ------------------------------------------

/**
 * The row's "what it lacks" line (no rule ids, engine names or actor ids). `reviewer` is who reviews
 * the hourly results: null when the viewer may review ("your review"), else the reviewer's name
 * (build review F6). Null when the line would only repeat the row's status (aesthetic A4).
 */
export function missingText(i: { reason: MissingReason; tests_missing: number }, reviewer: string | null = null): string | null {
  const n = i.tests_missing;
  switch (i.reason) {
    case 'waits_on_review': return reviewer ? `result waits on ${reviewer}’s review` : 'result waits on your review';
    case 'person_decides': return null;
    case 'rule_not_verified': return 'automated check not ready yet · a person can decide it';
    case 'needs_reconciliation_run': return 'needs a data comparison run · a person can decide it';
    case 'needs_more_tests': return `${n} more ${n === 1 ? 'test needs' : 'tests need'} evidence`;
    case 'needs_evidence': return `${n} ${n === 1 ? 'test needs' : 'tests need'} evidence`;
    case 'result_next_hour': return reviewer ? `results next hour, then ${reviewer}’s review` : 'results next hour, then your review';
  }
}

/**
 * The hero's explanation of a low number (D-106-19): "0 reviewed · 2 checks ran", plus ", N results
 * wait on <who>'s review" unless a Review button carries the count (glance G3). Null when the number
 * isn't low and nothing waits (no extra text on a healthy hero).
 */
export function heroInputsLine(
  s: { reviewed: number; checks_ran: number; awaiting_review: number },
  o: { covered: number; total: number; reviewButton: boolean; reviewer: string | null },
): string | null {
  const low = s.reviewed === 0 || (o.total > 0 && o.covered / o.total < 0.5);
  const ran = `${s.checks_ran} ${s.checks_ran === 1 ? 'check' : 'checks'} ran`;
  const wait = s.awaiting_review && !o.reviewButton
    ? `${s.awaiting_review} ${s.awaiting_review === 1 ? 'result waits' : 'results wait'} on ${o.reviewer ? `${o.reviewer}’s` : 'your'} review` : '';
  if (low) return `${s.reviewed} reviewed · ${ran}${wait ? ` · ${wait}` : ''}`;
  return wait || null;
}

/** A proposed result in words for the reviewer (the evaluator's rationale names tests; this never does). */
export function proposedResultWords(d: { result: string; proposed_by: string }, nameOf: (userId: string) => string): { result: string; basis: string } {
  const result = d.result === 'met' ? 'Met' : d.result === 'not_met' ? 'Not met' : d.result === 'not_applicable' ? 'Not applicable' : 'No evidence';
  if (!d.proposed_by.startsWith('system:')) return { result, basis: `recorded by ${nameOf(d.proposed_by)}` };
  const basis = d.result === 'met' ? 'every check for this point passed' : d.result === 'not_met' ? 'a check for this point failed' : 'no check result in its window';
  return { result, basis };
}

/** One cited piece of evidence in words ("Okta check · passed · Oct 17"; a file by its title). */
export function evidenceWords(r: { kind: string; title: string | null; status?: string | null; observed_at?: string | null; source?: string | null }, today: string): string {
  if (r.kind === 'check') {
    const st = r.status === 'pass' ? 'passed' : r.status === 'fail' ? 'failed' : 'couldn’t check';
    return `${r.source ? connectorWord(r.source) : 'Connector'} check · ${st}${r.observed_at ? ` · ${fmtDate(r.observed_at, today)}` : ''}`;
  }
  if (r.kind === 'reconciliation') return 'Data comparison run';
  return r.title ?? 'Evidence no longer available';
}
